import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Typography } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useAuth } from '../AuthContext';
import { dayKeyIST, timeLabel } from '../utils/reception';
import { formatPersonName } from '../utils/personName';

type BrokerVisit = {
  id: string; brokerId: string; name: string; phone10: string;
  agencyName: string | null; status: 'waiting' | 'with' | 'done' | 'left';
  checkedInAt: any; metByEmail: string | null; metByName: string | null;
};
type Project = {id: string; name: string};
type ProfileSubmission = {status: string; submittedAt: any};
const phoneDigits = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return (digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits).slice(0, 10);
};
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const GOLD = '#9d6f32';
const DARK = '#1c1b19';
const MUTED = '#5b5b5b';
const LINE = '#e8e3da';
const teamActionSx = {minHeight: 40, px: 2, borderRadius: 999, fontFamily: FONT,
  fontWeight: 700, fontSize: 13, textTransform: 'none'} as const;
const fieldSx = {width: '100%', boxSizing: 'border-box', height: 46,
  fontSize: 16, fontFamily: FONT, px: 1.5, border: '1px solid #d9d3c8',
  borderRadius: 2.5, outline: 'none', bgcolor: '#fff',
  '&:disabled': {bgcolor: '#f7f5f1', color: MUTED}} as const;
const labelSx = {fontFamily: FONT, fontSize: 13, fontWeight: 600,
  color: MUTED, display: 'block', mb: 0.75} as const;
const assessmentOptions = {
  project: [
    {value: 'unknown', label: 'Unknown'},
    {value: 'interested', label: 'Interested'},
    {value: 'active', label: 'Actively working'},
    {value: 'inactive', label: 'Inactive'},
  ],
  potential: [
    {value: 'unknown', label: 'Unknown'},
    {value: 'low', label: 'Low'},
    {value: 'medium', label: 'Medium'},
    {value: 'high', label: 'High'},
  ],
  agency: [
    {value: 'unknown', label: 'Not confirmed'},
    {value: 'independent', label: 'Independent'},
    {value: 'member', label: 'Agency member'},
    {value: 'head', label: 'Agency head'},
  ],
};

const AssessmentPill = ({selected, onClick, children}: {
  selected: boolean; onClick: () => void; children: React.ReactNode;
}) => <Box component="button" type="button" onClick={onClick} aria-pressed={selected}
  sx={{minHeight: 44, px: 2, borderRadius: 999, cursor: 'pointer', fontFamily: FONT,
    fontSize: 14, fontWeight: selected ? 700 : 500,
    border: selected ? '1px solid transparent' : '1px solid #d9d3c8',
    bgcolor: selected ? GOLD : '#fff', color: selected ? '#fff' : DARK,
    '&:focus-visible': {outline: '2px solid #2563eb', outlineOffset: 2}}}>
  {children}
</Box>;

export default function BrokerVisitPanel({mode}: {mode: 'desk' | 'team'}) {
  const {currentUser, roleId} = useAuth();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [agency, setAgency] = useState('');
  const [match, setMatch] = useState<{id: string; name: string; agency: string} | null>(null);
  const [visits, setVisits] = useState<BrokerVisit[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [finish, setFinish] = useState<BrokerVisit | null>(null);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [projectStatuses, setProjectStatuses] = useState<Record<string, string>>({});
  const [potential, setPotential] = useState('unknown');
  const [agencyRole, setAgencyRole] = useState('unknown');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [noticeSeverity, setNoticeSeverity] = useState<'success' | 'info' | 'warning'>('success');
  const [error, setError] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  const canMeet = mode === 'team' && roleId !== 'front-desk';

  useEffect(() => onSnapshot(query(collection(db, 'brokerVisits'),
    where('dayKey', '==', dayKeyIST())), (snap) => {
    setVisits(snap.docs.map((d) => ({id: d.id, ...(d.data() as any)}))
      .sort((a, b) => (a.checkedInAt?.toMillis?.() || 0) -
        (b.checkedInAt?.toMillis?.() || 0)));
  }, (err) => setError(err?.message || 'Could not load broker visits.')), []);
  useEffect(() => onSnapshot(collection(db, 'projects'), (snap) =>
    setProjects(snap.docs.map((d) => ({id: d.id, name: String(d.data().name || d.id)}))),
  () => setProjects([])), []);
  useEffect(() => {
    setMatch(null);
    setName('');
    setAgency('');
    if (!/^[6-9]\d{9}$/.test(phone)) return;
    let active = true;
    getDocs(query(collection(db, 'brokers'), where('phoneE164', '==', `+91${phone}`), limit(1)))
      .then((snap) => {
        if (!active || snap.empty) return;
        const b = snap.docs[0].data();
        const found = {id: snap.docs[0].id, name: String(b.brokerName || ''),
          agency: String(b.agencyName || '')};
        setMatch(found);
        setName(found.name);
        setAgency(found.agency);
      });
    return () => { active = false; };
  }, [phone]);
  const invoke = async (action: string, visit: BrokerVisit, assessment?: any) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await httpsCallable(functions, 'brokerVisitAction')({visitId: visit.id, action, assessment});
      if (action === 'finish') setFinish(null);
      setNotice(action === 'meet' ? `${visit.name} is now in a meeting.` :
        `${visit.name}'s visit was recorded.`);
    } catch (err: any) { setError(err?.message || 'Could not update broker visit.'); }
    finally { setBusy(false); }
  };
  const checkIn = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await httpsCallable(functions, 'brokerVisitCheckIn')({
        phone, name: match ? name : formatPersonName(name), agencyName: agency,
      });
      setPhone(''); setName(''); setAgency(''); setMatch(null);
      setNotice('Broker checked in.');
    } catch (err: any) { setError(err?.message || 'Could not check in broker.'); }
    finally { setBusy(false); }
  };
  const sendForm = async (visit: BrokerVisit) => {
    setBusy(true); setError(''); setNotice(''); setManualUrl('');
    try {
      const result: any = await httpsCallable(functions, 'brokerProfileInvite')({brokerId: visit.brokerId});
      if (result.data?.sent) {
        setNoticeSeverity('info');
        setNotice(`Profile link submitted to WhatsApp for ${visit.name}. Delivery is not confirmed yet.`);
      }
      else {
        setManualUrl(String(result.data?.manualUrl || ''));
        setNoticeSeverity('warning');
        setNotice(String(result.data?.sendError || 'WhatsApp could not send automatically.') +
          ' Open WhatsApp to send the prepared link.');
      }
    } catch (err: any) { setError(err?.message || 'Could not create profile link.'); }
    finally { setBusy(false); }
  };
  const waiting = visits.filter((v) => v.status === 'waiting');
  const meeting = visits.filter((v) => v.status === 'with');
  const canCheckIn = !busy && /^[6-9]\d{9}$/.test(phone) &&
    name.trim().length >= 2 && !name.includes('--');
  if (mode === 'team' && waiting.length === 0 && meeting.length === 0) return null;
  return <Box component="section" aria-label="Broker visit"
    sx={mode === 'desk' ? {fontFamily: FONT} : {border: `1px solid ${LINE}`,
      borderRadius: 3, p: {xs: 2, md: 2.5}, bgcolor: '#fff', mt: 2}}>
    {mode === 'team' && <Stack direction={{xs: 'column', sm: 'row'}}
      alignItems={{sm: 'center'}} justifyContent="space-between" gap={1} sx={{mb: 2}}>
      <Typography component="h2" sx={{fontFamily: FONT, fontSize: 18, fontWeight: 700}}>
        Broker visits
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Box sx={{px: 1.25, py: 0.5, borderRadius: 999, bgcolor: '#fff7e9',
          color: '#a35f00', fontFamily: FONT, fontSize: 12.5, fontWeight: 700}}>
          Waiting {waiting.length}
        </Box>
        <Box sx={{px: 1.25, py: 0.5, borderRadius: 999, bgcolor: '#edf4fb',
          color: '#23689e', fontFamily: FONT, fontSize: 12.5, fontWeight: 700}}>
          In meeting {meeting.length}
        </Box>
      </Stack>
    </Stack>}
    {mode === 'desk' && <Box component="form" onSubmit={(event) => {event.preventDefault(); if (canCheckIn) checkIn();}}
      sx={{bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 4,
        p: {xs: 2, md: 3}, display: 'flex', flexDirection: 'column', gap: 2.25}}>
      <Box>
        <Typography component="label" htmlFor="broker-visit-phone" sx={labelSx}>Broker mobile number *</Typography>
        <Box component="input" id="broker-visit-phone" type="tel" inputMode="numeric"
          autoComplete="tel-national" required value={phone.length > 5 ?
            `${phone.slice(0, 5)} ${phone.slice(5)}` : phone}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPhone(phoneDigits(event.target.value))}
          placeholder="12345 67890" aria-invalid={phone.length === 10 && !/^[6-9]\d{9}$/.test(phone)}
          sx={{...fieldSx, height: 54, fontSize: 20, px: 2, letterSpacing: 1,
            border: `2px solid ${phone.length === 10 && !/^[6-9]\d{9}$/.test(phone) ? '#b3261e' : GOLD}`, borderRadius: 3}} />
        {phone.length === 10 && !/^[6-9]\d{9}$/.test(phone) &&
          <Typography role="alert" sx={{fontFamily: FONT, fontSize: 12.5, mt: 0.75, color: '#b3261e'}}>
            Enter a mobile number starting with 6, 7, 8 or 9.
          </Typography>}
      </Box>
      {match && <Box sx={{border: `1px solid ${LINE}`, borderRadius: 2.5,
        bgcolor: '#fffcf7', px: 2, py: 1.5}}>
        <Typography sx={{fontFamily: FONT, fontSize: 14, fontWeight: 700}}>{match.name}</Typography>
        <Typography sx={{fontFamily: FONT, fontSize: 12.5, color: MUTED}}>
          Existing broker{match.agency ? ` · ${match.agency}` : ''}. This visit will use their record.
        </Typography>
      </Box>}
      <Box>
        <Typography component="label" htmlFor="broker-visit-name" sx={labelSx}>Broker name *</Typography>
        <Box component="input" id="broker-visit-name" required value={name} disabled={!!match}
          autoCapitalize="words"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          onBlur={() => { if (!match) setName((current) => formatPersonName(current)); }}
          aria-invalid={name.includes('--')} sx={fieldSx} />
        {name.includes('--') && <Typography role="alert"
          sx={{fontFamily: FONT, fontSize: 12.5, mt: 0.75, color: '#b3261e'}}>
          Enter a real name without --.
        </Typography>}
      </Box>
      <Box>
        <Typography component="label" htmlFor="broker-visit-agency" sx={labelSx}>Agency name (if any)</Typography>
        <Box component="input" id="broker-visit-agency" value={agency} disabled={!!match}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAgency(event.target.value)} sx={fieldSx} />
      </Box>
      <Button type="submit" variant="contained" disableElevation disabled={!canCheckIn}
        sx={{height: 56, borderRadius: 3.5, bgcolor: DARK, fontFamily: FONT,
          fontWeight: 700, fontSize: 17, textTransform: 'none', '&:hover': {bgcolor: '#33312d'}}}>
        {busy ? 'Checking in…' : 'Check in'}
      </Button>
    </Box>}
    {error && <Alert severity="error" sx={{mb: 1}}>{error}</Alert>}
    {notice && <Alert severity={noticeSeverity} sx={{mb: 1}}>{notice}
      {manualUrl && <Button href={manualUrl} target="_blank" rel="noopener noreferrer"
        sx={{ml: 1}}>Open WhatsApp</Button>}</Alert>}
    {mode === 'desk' && waiting.length > 0 && <Box component="section"
      aria-label="Brokers waiting at reception" sx={{bgcolor: '#fff8ec',
        border: '1px solid #f0c98f', borderRadius: 2, p: 2, mt: 2}}>
      <Typography component="h2" sx={{fontFamily: FONT, fontSize: 16,
        fontWeight: 700, mb: 1}}>Waiting at reception ({waiting.length})</Typography>
      {waiting.map((visit) => <VisitRow key={visit.id} visit={visit} busy={busy} desk
        actions={<>
          <ProfileFormAction visit={visit} busy={busy} showHistory={false}
            onSend={() => sendForm(visit)} />
          <Button onClick={() => invoke('left', visit)} disabled={busy}>Broker left</Button>
        </>} />)}
    </Box>}
    {mode === 'team' && <Box sx={{display: 'grid', gap: 2,
      gridTemplateColumns: waiting.length > 0 && meeting.length > 0 ?
        {xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))'} : '1fr'}}>
      {waiting.length > 0 && <Box component="section" aria-label="Brokers waiting at reception"
        sx={{bgcolor: '#fff8ed', border: '1px solid #f0c98f', borderRadius: 2.5, p: 2}}>
        <Typography component="h3" sx={{fontFamily: FONT, fontSize: 15, fontWeight: 700,
          color: '#a35f00', mb: 1.25}}>Waiting at reception ({waiting.length})</Typography>
        <Stack spacing={1.25}>
          {waiting.map((visit) => <VisitRow key={visit.id} visit={visit} busy={busy}
            actions={<>
              <ProfileFormAction visit={visit} busy={busy} showHistory
                onSend={() => sendForm(visit)} />
              <Button onClick={() => invoke('meet', visit)} disabled={busy}
                variant="contained" sx={{...teamActionSx, bgcolor: GOLD}}>
                I'll meet
              </Button>
            </>} />)}
        </Stack>
      </Box>}
      {meeting.length > 0 && <Box component="section" aria-label="Brokers in a meeting"
        sx={{bgcolor: '#edf4fb', border: '1px solid #cbdff0', borderRadius: 2.5, p: 2}}>
        <Typography component="h3" sx={{fontFamily: FONT, fontSize: 15, fontWeight: 700,
          color: '#23689e', mb: 1.25}}>In meeting ({meeting.length})</Typography>
        <Stack spacing={1.25}>
          {meeting.map((visit) => <VisitRow key={visit.id} visit={visit} busy={busy}
            actions={visit.metByEmail === currentUser?.email ? <>
              <ProfileFormAction visit={visit} busy={busy} showHistory
                onSend={() => sendForm(visit)} />
              <Button variant="contained" disabled={busy} sx={{...teamActionSx, bgcolor: GOLD}}
                onClick={() => {
                  setFinish(visit); setProjectIds([]); setProjectStatuses({});
                  setPotential('unknown'); setAgencyRole('unknown'); setReason('');
                }}>Finish broker visit</Button>
            </> : <Typography variant="body2" sx={{fontFamily: FONT, color: MUTED}}>
              With {visit.metByName}
            </Typography>} />)}
        </Stack>
      </Box>}
    </Box>}
    <Dialog open={!!finish} onClose={() => !busy && setFinish(null)} fullWidth maxWidth="sm"
      PaperProps={{sx: {borderRadius: 4, fontFamily: FONT, overflow: 'hidden'}}}>
      <DialogTitle sx={{fontFamily: FONT, fontSize: 22, fontWeight: 700,
        color: DARK, px: {xs: 2.5, sm: 3}, py: 2.5, borderBottom: `1px solid ${LINE}`}}>
        Finish broker visit · {finish?.name}
      </DialogTitle>
      <DialogContent sx={{px: {xs: 2.5, sm: 3}, py: 2.5}}>
        <Stack spacing={2.75} sx={{pt: 0.5}}>
          <Typography sx={{fontFamily: FONT, fontSize: 14, color: MUTED}}>
            Complete the internal assessment after meeting the broker. These answers are not shown on the broker form.
          </Typography>
          <Box role="group" aria-label="Projects discussed">
            <Typography sx={labelSx}>Projects discussed</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {projects.map((p) => <AssessmentPill key={p.id} selected={projectIds.includes(p.id)}
                onClick={() => setProjectIds((old) => old.includes(p.id) ?
                  old.filter((id) => id !== p.id) : [...old, p.id])}>{p.name}</AssessmentPill>)}
            </Stack>
          </Box>
          {projectIds.map((id) => <Box key={id} role="group"
            aria-label={`${projects.find((p) => p.id === id)?.name || id} working status`}>
            <Typography sx={labelSx}>
              {projects.find((p) => p.id === id)?.name || id} · working status
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {assessmentOptions.project.map((option) => <AssessmentPill key={option.value}
                selected={(projectStatuses[id] || 'unknown') === option.value}
                onClick={() => setProjectStatuses((old) => ({...old, [id]: option.value}))}>
                {option.label}
              </AssessmentPill>)}
            </Stack>
          </Box>)}
          <Box role="group" aria-label="Broker potential">
            <Typography sx={labelSx}>Broker potential</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {assessmentOptions.potential.map((option) => <AssessmentPill key={option.value}
                selected={potential === option.value} onClick={() => setPotential(option.value)}>
                {option.label}
              </AssessmentPill>)}
            </Stack>
          </Box>
          <Box role="group" aria-label="Agency relationship">
            <Typography sx={labelSx}>Agency relationship (staff assessment)</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {assessmentOptions.agency.map((option) => <AssessmentPill key={option.value}
                selected={agencyRole === option.value} onClick={() => setAgencyRole(option.value)}>
                {option.label}
              </AssessmentPill>)}
            </Stack>
          </Box>
          <Box>
            <Typography component="label" htmlFor="broker-visit-reason" sx={labelSx}>
              Reason / meeting notes
            </Typography>
            <Box component="textarea" id="broker-visit-reason" rows={3} value={reason}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setReason(event.target.value)}
              sx={{...fieldSx, height: 'auto', minHeight: 96, py: 1.5, resize: 'vertical',
                lineHeight: 1.5, '&:focus-visible': {outline: '2px solid #2563eb'}}} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{px: {xs: 2.5, sm: 3}, py: 2, gap: 1,
        borderTop: `1px solid ${LINE}`}}>
        <Button onClick={() => setFinish(null)} disabled={busy}
          sx={{minHeight: 44, px: 2, borderRadius: 999, color: DARK,
            fontFamily: FONT, fontWeight: 600, textTransform: 'none'}}>Cancel</Button>
        <Button onClick={() => finish && invoke('finish', finish,
          {projectWork: projectIds.map((projectId) => ({projectId,
            status: projectStatuses[projectId] || 'unknown'})), potential, agencyRole, reason})}
          disabled={busy} variant="contained" disableElevation
          sx={{minHeight: 46, px: 2.5, borderRadius: 999, bgcolor: DARK,
            fontFamily: FONT, fontWeight: 700, textTransform: 'none',
            '&:hover': {bgcolor: '#33312d'}}}>Save · finish</Button>
      </DialogActions>
    </Dialog>
  </Box>;
}

function ProfileFormAction({visit, busy, showHistory, onSend}: {
  visit: BrokerVisit; busy: boolean; showHistory: boolean; onSend: () => void;
}) {
  const [latest, setLatest] = useState<ProfileSubmission | null>(null);
  const [historyError, setHistoryError] = useState(false);
  useEffect(() => {
    if (!showHistory || !visit.brokerId) return;
    setLatest(null);
    setHistoryError(false);
    return onSnapshot(query(collection(db, 'brokerProfileSubmissions'),
      where('brokerId', '==', visit.brokerId)), (snap) => {
      const submissions = snap.docs.map((docSnap) => docSnap.data() as ProfileSubmission);
      submissions.sort((a, b) => (b.submittedAt?.toMillis?.() || 0) -
        (a.submittedAt?.toMillis?.() || 0));
      setLatest(submissions[0] || null);
    }, () => setHistoryError(true));
  }, [showHistory, visit.brokerId]);
  const submittedDate = latest?.submittedAt?.toDate?.();
  const dateLabel = submittedDate instanceof Date && !Number.isNaN(submittedDate.getTime()) ?
    new Intl.DateTimeFormat('en-IN', {timeZone: 'Asia/Kolkata', day: 'numeric',
      month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'}).format(submittedDate) : null;
  const statusLabel = latest?.status === 'pending' ? 'Pending approval' :
    latest?.status === 'approved' ? 'Approved' :
      latest?.status === 'rejected' ? 'Rejected' : latest?.status || '';
  return <>
    <Button disabled={busy} onClick={onSend} variant={showHistory ? 'outlined' : 'text'}
      sx={showHistory ? {...teamActionSx, borderColor: '#d9d3c8', color: GOLD,
        bgcolor: '#fff'} : undefined}>Send profile form</Button>
    {showHistory && latest && <Typography variant="caption" sx={{fontFamily: FONT,
      color: MUTED, lineHeight: 1.35, flexBasis: '100%', order: 1}}>
      Filled {dateLabel || 'previously'} · {statusLabel}
    </Typography>}
    {showHistory && historyError && <Typography variant="caption" sx={{fontFamily: FONT,
      color: '#b3261e', flexBasis: '100%', order: 1}}>Form history unavailable</Typography>}
  </>;
}

function VisitRow({visit, actions, desk = false}: {
  visit: BrokerVisit; busy: boolean; actions: React.ReactNode; desk?: boolean;
}) {
  return <Box sx={desk ? {borderTop: '1px solid #eadac2', py: 1.25,
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5} :
    {border: `1px solid ${LINE}`, borderRadius: 2, p: {xs: 1.5, sm: 2},
      bgcolor: '#fff', display: 'flex', flexDirection: 'column', gap: 1.25}}>
    <Box sx={{flex: 1, minWidth: 200}}>
      <Typography sx={{fontFamily: FONT, fontSize: desk ? 14 : 16, fontWeight: 700}}>{visit.name}</Typography>
      <Typography sx={{fontFamily: FONT, fontSize: desk ? 12.5 : 14, color: MUTED}}>
        {[visit.agencyName, visit.phone10, `Arrived ${timeLabel(visit.checkedInAt)}`]
          .filter(Boolean).join(' · ')}
      </Typography>
    </Box>
    <Stack direction="row" flexWrap="wrap" gap={0.5}
      sx={desk ? {'& .MuiButton-root': {minHeight: 36, px: 1.5, borderRadius: 1.5,
        bgcolor: '#fff', border: '1px solid #d9d3c8', color: DARK,
        fontFamily: FONT, fontSize: 12.5, fontWeight: 700, textTransform: 'none'}} :
        {pt: 1.25, borderTop: `1px solid ${LINE}`, alignItems: 'flex-start', gap: 1}}>
      {actions}
    </Stack>
  </Box>;
}
